import { useSyncExternalStore } from "react";

// Per-browser Meeting settings, as in Teams' Settings > Notifications and General. Quiet hours live on the server (preferences).

export interface MeetingSettings {
    /** Play a chime for new messages. */
    sound: boolean;
    /** Show a desktop banner for new messages when the tab isn't in front. */
    banners: boolean;
    /** Put the sender and message text in banners (off: just "New message"). */
    previews: boolean;
    /** Enter sends (Shift+Enter for a new line). Off: Ctrl+Enter sends and Enter makes a new line. */
    enterToSend: boolean;
}

const DEFAULTS: MeetingSettings = { sound: true, banners: true, previews: true, enterToSend: true };
const KEY = "fg.connect.settings";
const listeners = new Set<() => void>();
let cache: MeetingSettings | null = null;

export function readSettings(): MeetingSettings {
    if (cache) return cache;
    let out = DEFAULTS;
    try {
        const raw = window.localStorage.getItem(KEY);
        if (raw) {
            const j = JSON.parse(raw) as Partial<MeetingSettings>;
            out = {
                sound: j.sound ?? DEFAULTS.sound,
                banners: j.banners ?? DEFAULTS.banners,
                previews: j.previews ?? DEFAULTS.previews,
                enterToSend: j.enterToSend ?? DEFAULTS.enterToSend
            };
        }
    } catch {
        /* damaged value: defaults */
    }
    cache = out;
    return out;
}

export function updateSettings(patch: Partial<MeetingSettings>) {
    const next = { ...readSettings(), ...patch };
    cache = next;
    try { window.localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* kept for this session */ }
    listeners.forEach((l) => l());
}

export function useSettings(): MeetingSettings {
    return useSyncExternalStore(
        (cb) => {
            listeners.add(cb);
            const onStorage = (e: StorageEvent) => { if (e.key === KEY) { cache = null; cb(); } };
            window.addEventListener("storage", onStorage);
            return () => { listeners.delete(cb); window.removeEventListener("storage", onStorage); };
        },
        readSettings
    );
}
